// The Forge's own words: labels, and the few lines the developer page doesn't already have. Every claim maps to a
// claims-ledger row (docs/launch/claims.md, ids in the comments). No speed words here: those live only in
// launch-copy.ts (test/copy.test.ts). CLI commands are allowed in this zone only (test/zones.test.ts).

/** One step of the path. `when` is a launch-copy gate (the step is held until it fills); `code` picks a guide block. */
export interface PathStep {
  title: string;
  body?: string;
  /** the body is this developer-page block's first paragraph (launch-copy.ts DEV_BLOCKS_AFTER, by id) */
  copy?: string;
  /** a substring of the launch-copy GUIDE code block to show */
  code?: string;
  when?: string;
}

export const PATH_STEPS: PathStep[] = [
  // P14, P15, P17: the developer page's "Nothing to rewrite" paragraph (launch-copy.ts DEV_BLOCKS_AFTER "rewrite")
  { title: "Nothing to rewrite", copy: "rewrite" },
  // H1, H2 (blocked: prod hosts the download; sign-in email)
  { title: "Install the CLI and sign in", body: "Once. You don't need an npm account.", code: "thrdm login", when: "[[?guide-public|yes]]" },
  // H3 (blocked with H1, H2 and the builds domain)
  { title: "Let your AI agent help", body: "The agent can use the pack and deploy for you. Nothing goes up until you say yes.", code: "claude mcp add", when: "[[?mcp-public|yes]]" },
  // H4 (the zip is hosted by prod: launch-copy's {{haunt: …}} fact)
  { title: "Start from The Haunt", body: "The haunt template runs in your browser. Every save reloads the page.", code: "npm run dev", when: "{{haunt: banger.sh/downloads/haunt-template.zip}}" },
  // P4 (blocked: the builds domain, prod's certificate)
  { title: "Deploy", body: "Every deploy is a new version. If something breaks, roll back.", code: "thrdm deploy", when: "{{builds-domain}}" },
];

export const TEMPLATE_COPY = {
  eyebrow: "Start here",
  title: "The Haunt",
  // H4, from the guide's description (open-haunt-howto.md)
  line: "A small 3D game that runs in a browser. You walk through a graveyard at night, follow the jack-o'-lanterns up to an old house, and go inside, where something is waiting.",
  alt: "The Haunt template: a graveyard gate in fog, a haunted house behind it",
  download: "Download the template",
  haunt: "See Open Haunt",
};

export const WAYS_COPY = {
  eyebrow: "For creators",
  title: "Your games, where players find them.",
  // the platform rule (PLAN.md: creators 18+)
  age: "Creators are 18 or older.",
  signin: "Sign in with your email. No password, and no account needed to look around.",
  start: "Sign in to start",
  questions: "Questions",
  pathTitle: "From your folder to Banger",
  copy: "Copy",
  copied: "Copied",
  /** a "why" card's paragraphs after its first */
  more: "More",
};

/**
 * "Ways in": the Open Haunt offer. claims.md H10: submissions 23 Oct – 6 Nov, the row up until 10 Nov (HAUNT_RULES "Dates"; owner extended the season on 3 Nov).
 * developer-page.md's "Open Haunt runs 23 October – 6 November" reads as the season's end, so the Forge says it this way
 * (request to the launch track to fix the doc and launch-copy.ts DEV_GET).
 */
export const HAUNT_OFFER = {
  para: "**Made something spooky?** Ship it on Banger by Fri 6 November and it's in the Halloween row, up until Tue 10 November.",
  action: { text: "Open Haunt", href: "/haunt" },
  when: undefined as string | undefined,
};

/**
 * Each benchmarked game's art for its beat in the numbers, keyed by the game's name as the copy has it with names on
 * (launch-copy.ts DEV_TABLE). Files in zones/developers/bench/: Punk Drive and Dystopia from the launcher's featured
 * art, Gigacity from the catalogue, the Plaza ours, Nightrider a frame from our own engine (claims.md S10). Off Road
 * Earth has no art of ours, so it gets a name card in its own colours.
 */
export interface BenchArt {
  art?: string;
  logo?: string;
  /** the card's tint, behind the art and on the name card */
  tint: string;
}

const BENCH = "/zones/developers/bench";
export const BENCH_ART: Record<string, BenchArt> = {
  Nightrider: { art: `${BENCH}/nightrider.jpg`, tint: "#ff3b2f" },
  "The Plaza": { art: `${BENCH}/plaza.jpg`, tint: "#d8b47a" },
  "Punk Drive": { art: `${BENCH}/punk-drive.jpg`, logo: `${BENCH}/punk-drive-title.png`, tint: "#ff3ec8" },
  Dystopia: { art: `${BENCH}/dystopia.jpg`, logo: `${BENCH}/dystopia-title.png`, tint: "#38e07b" },
  Gigacity: { art: `${BENCH}/gigacity.jpg`, logo: `${BENCH}/gigacity-title.png`, tint: "#5aa8ff" },
  "Off Road Earth": { tint: "#c98a3a" },
};

/** The numbers' own words come from launch-copy.ts (its table head and row kinds); these are the zone's labels. */
export const BENCH_COPY = {
  vs: "vs",
  /** under a time row (ms, s), where the shorter bar is ahead */
  lower: "lower is better",
  table: "Every number as a table",
};
